import { CartLinesUpdateEvent } from '@shopify/events';

class NushCartMonthSelector extends HTMLElement {
  #busy = false;

  setMonths(targetMonths) {
    return this.#setMonths(targetMonths);
  }

  async #post(path, body) {
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
    return data;
  }

  async #cart() {
    const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
    return response.json();
  }

  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  #emitRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
  }

  async #setMonths(targetMonths) {
    if (this.#busy || targetMonths > 99) return;
    const currentMonths = Number(this.dataset.currentMonths || 1);
    const currentVariant = Number(this.dataset.currentVariant || this.dataset.currentKey?.split(':')[0]);
    const currentQuantity = Number(this.dataset.currentQuantity || 1);
    const currentPlan = this.dataset.currentPlan || '';
    const currentGroup = this.dataset.currentGroup || '';
    const subscriptionIntent = this.dataset.subscriptionIntent === 'true';
    const line = Number(this.dataset.line || 1);
    const sectionIds = this.#sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    const status = this.querySelector('[data-nush-month-status]');
    const buttons = this.querySelectorAll('button');
    let currentItems = [];

    this.#busy = true;
    this.dataset.busy = 'true';
    buttons.forEach((button) => { button.disabled = true; });
    if (status) status.textContent = targetMonths <= 0 ? 'Removing item' : `Changing supply to ${targetMonths} month${targetMonths === 1 ? '' : 's'}`;

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: targetMonths <= 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ merchandiseId: String(currentVariant), quantity: targetMonths }],
      promise: deferred.promise,
    }));

    try {
      const cartBefore = await this.#cart();
      currentItems = currentGroup
        ? cartBefore.items.filter((item) => item.properties?._nush_month_group === currentGroup)
        : cartBefore.items.filter((item) => item.key === this.dataset.currentKey);
      const updates = Object.fromEntries(currentItems.map((item) => [item.key, 0]));
      let finalResponse;
      if (targetMonths <= 0) {
        finalResponse = await this.#post(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
          updates,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
      } else {
        await this.#post(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, { updates });
        const items = [];
        if (targetMonths <= 3) {
          const targetVariant = Number(this.getAttribute(`data-variant-${targetMonths}`));
          if (!targetVariant) throw new Error(`${targetMonths}-month option is unavailable`);
          const item = { id: targetVariant, quantity: 1 };
          const targetPlan = subscriptionIntent ? this.getAttribute(`data-plan-${targetMonths}`) : '';
          if (targetPlan) item.selling_plan = Number(targetPlan);
          if (subscriptionIntent && !targetPlan) item.properties = { _nush_subscription_intent: 'true' };
          items.push(item);
        } else {
          const baseVariant = Number(this.getAttribute('data-variant-3'));
          const extraVariant = Number(this.getAttribute('data-variant-1'));
          if (!baseVariant || !extraVariant) throw new Error('Extended month options are unavailable');
          const group = `nush-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
          const base = { id: baseVariant, quantity: 1, properties: { _nush_month_group: group, _nush_month_role: 'base' } };
          const extra = { id: extraVariant, quantity: targetMonths - 3, properties: { _nush_month_group: group, _nush_month_role: 'extra' } };
          if (subscriptionIntent) {
            const basePlan = this.getAttribute('data-plan-3');
            const extraPlan = this.getAttribute('data-plan-quarterly-1');
            if (!basePlan || !extraPlan) throw new Error('Quarterly subscription option is unavailable');
            base.selling_plan = Number(basePlan);
            extra.selling_plan = Number(extraPlan);
            base.properties._nush_subscription_intent = 'true';
            extra.properties._nush_subscription_intent = 'true';
          }
          items.push(base, extra);
        }
        finalResponse = await this.#post(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
      }
      const cart = await this.#cart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: finalResponse.sections, didError: false, source: 'nush-cart-month-selector' },
      });
      this.#emitRefresh();
      this.#busy = false;
      delete this.dataset.busy;
      this.querySelectorAll('button').forEach((button) => { button.disabled = false; });
    } catch (error) {
      console.error('[nush-cart-month-selector]', error);
      try {
        const cartAfterError = await this.#cart();
        const anyOriginalLineRemains = currentItems.some((beforeItem) => cartAfterError.items.some((afterItem) => afterItem.key === beforeItem.key));
        if (!anyOriginalLineRemains && currentItems.length) {
          const rollbackItems = currentItems.map((item) => {
            const rollback = { id: item.variant_id, quantity: item.quantity };
            if (item.properties && Object.keys(item.properties).length) rollback.properties = item.properties;
            const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
            if (sellingPlan) rollback.selling_plan = Number(sellingPlan);
            return rollback;
          });
          await this.#post(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, { items: rollbackItems });
        }
      } catch (rollbackError) {
        console.error('[nush-cart-month-selector] rollback failed', rollbackError);
      }
      deferred.reject(error);
      this.#emitRefresh();
      if (status) status.textContent = error.message || 'Unable to update supply';
      this.#busy = false;
      delete this.dataset.busy;
      buttons.forEach((button) => { button.disabled = false; });
    }
  }
}

if (!customElements.get('nush-cart-month-selector')) {
  customElements.define('nush-cart-month-selector', NushCartMonthSelector);
}

if (!globalThis.__nushCartMonthSelectorClickBound) {
  globalThis.__nushCartMonthSelectorClickBound = true;
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const removeButton = event.target.closest('[data-nush-remove-month-group]');
    if (removeButton) {
      const groupedSelector = removeButton.closest('.cart-items__table-row')?.querySelector('nush-cart-month-selector');
      if (groupedSelector instanceof NushCartMonthSelector) groupedSelector.setMonths(0);
      return;
    }
    const button = event.target.closest('[data-nush-month-decrease], [data-nush-month-increase]');
    const selector = button?.closest('nush-cart-month-selector');
    if (!selector || !(selector instanceof NushCartMonthSelector)) return;
    const currentMonths = Number(selector.dataset.currentMonths || 1);
    selector.setMonths(currentMonths + (button.hasAttribute('data-nush-month-increase') ? 1 : -1));
  });
}
